import { ApolloError } from 'apollo-client';

import type { Mark } from '@atlaskit/editor-prosemirror/model';

import {
	RelayError as ApolloErrorInterop,
	type RelayGraphQLError,
} from '@confluence/relay-errors/entry-points/RelayError.ts';
import { getValidDate } from '@confluence/comments-util/entry-points/dateUtils.ts';
import type { AnnotationStatus } from '@confluence/comments-data/entry-points/useCommentsData.ts';
import type {
	CommentData,
	ReplyData,
} from '@confluence/comments-data-manager/entry-points/commentDataManagerUtils.ts';
import type {
	CommentInlineLocation,
	CommentFooterLocation,
} from '@confluence/comments-data-manager/entry-points/useAllCommentsData.ts';
import { SortValues } from './useCommentsPanel.ts';

export enum LocationValue {
	PANEL = 'panel',
	FOOTER = 'footer',
	STANDALONE = 'standalone',
}

const getCommentResolvedOrCreatedTime = (comment: CommentData): number => {
	const commentLocation = comment.location;
	const resolvedTime =
		(commentLocation as CommentInlineLocation)?.inlineResolveProperties?.resolvedTime ||
		(commentLocation as CommentFooterLocation)?.commentResolveProperties?.resolvedTime;
	return resolvedTime || getValidDate(comment.createdAtNonLocalized).getTime();
};

export const getSortedCommentThreads = (
	currentSort: SortValues,
	commentThreads: CommentData[],
): CommentData[] => {
	if (currentSort === SortValues.MOST_RECENT) {
		return [...commentThreads].sort(
			(a, b) => getCommentResolvedOrCreatedTime(b) - getCommentResolvedOrCreatedTime(a),
		);
	}

	return commentThreads;
};

export const getAnnotationThreadKeys = (annotations: AnnotationStatus[]): string[] =>
	annotations.map((annotation) => annotation.threadKey);

export const getMarkIds = (marks: Mark[] = []): any[] => marks.map((mark) => mark.attrs.id);

export const getCommentsSortedOldestToNewest = (
	commentA: CommentData | ReplyData,
	commentB: CommentData | ReplyData,
): number =>
	getValidDate(commentA?.createdAtNonLocalized).getTime() -
	getValidDate(commentB?.createdAtNonLocalized).getTime();

// Used to determine if a GQL query error only contains reactionsSummary errors.
// If true, we can ignore the error and still render the comments data that's fetched successfully.
export const onlyHasReactionsSummaryErrors = (error: any): boolean => {
	if (!error) {
		// if falsy, consider it as NOT containing non-reactionsSummary errors
		return true;
	} else if (error instanceof ApolloErrorInterop || error instanceof ApolloError) {
		// TODO we will need to migrate to ApolloRelayInterops to get proper error type of ApolloErrorInterop instead of ApolloError
		// after migration, we can remove the instanceof ApolloError check here
		const hasOtherGqlErrors =
			error.graphQLErrors?.filter((e: RelayGraphQLError) => !e.path?.includes('reactionsSummary'))
				?.length > 0;

		return !hasOtherGqlErrors && !error.networkError;
	} else {
		// if truthy but not an ApolloErrorInterop, consider it as containing non-reactionsSummary errors
		return false;
	}
};
